import { useEffect, useState, type ReactNode } from "react";
import { Link, Route, Switch, useLocation } from "wouter";
import { AdviceBench } from "./AdviceBench";
import { AdvisorSimulator } from "./AdvisorSimulator";
import { AgentDesk } from "./AgentDesk";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { FlowReview } from "./FlowReview";
import { Inbound } from "./Inbound";
import { Outbound } from "./Outbound";
import { Settings } from "./Settings";
import { TestAgent } from "./TestAgent";
import { apiFetch } from "./lib/api";
import "./styles/index.css";

// Per-route error boundary. Each top-level route gets its own boundary
// so a crash in /agent-desk can't blank /inbound or /settings — the
// agent navigates away and the failed surface is the only thing
// affected.
function RouteBoundary({ label, children }: { label: string; children: ReactNode }) {
  return <ErrorBoundary label={label}>{children}</ErrorBoundary>;
}

/**
 * Imperative redirect for renamed routes. Renders nothing — just nudges
 * wouter's location hook so old bookmarks survive (/copilot → /agent-desk).
 */
function Redirect({ to }: { to: string }) {
  const [, setLocation] = useLocation();
  useEffect(() => {
    setLocation(to, { replace: true });
  }, [to, setLocation]);
  return null;
}

export function App() {
  return (
    <Switch>
      <Route path="/inbound">
        {() => <RouteBoundary label="Inbound"><Inbound /></RouteBoundary>}
      </Route>
      <Route path="/inbound/:id">
        {(params) => (
          <RouteBoundary label="Inbound">
            <Inbound callId={Number(params.id)} />
          </RouteBoundary>
        )}
      </Route>
      <Route path="/outbound">
        {() => <RouteBoundary label="Outbound"><Outbound /></RouteBoundary>}
      </Route>
      <Route path="/outbound/:id">
        {(params) => (
          <RouteBoundary label="Outbound">
            <Outbound callId={Number(params.id)} />
          </RouteBoundary>
        )}
      </Route>
      <Route path="/agent-desk/simulator">
        {() => (
          <RouteBoundary label="Advisor Simulator">
            <AdvisorSimulator />
          </RouteBoundary>
        )}
      </Route>
      <Route path="/agent-desk">
        {() => <RouteBoundary label="Agent Desk"><AgentDesk /></RouteBoundary>}
      </Route>
      <Route path="/agent-desk/:id">
        {(params) => (
          <RouteBoundary label="Agent Desk">
            <AgentDesk callId={Number(params.id)} />
          </RouteBoundary>
        )}
      </Route>
      {/* Backward compat: /copilot bookmarks redirect to /agent-desk. */}
      <Route path="/copilot">{() => <Redirect to="/agent-desk" />}</Route>
      <Route path="/copilot/:id">
        {(params) => <Redirect to={`/agent-desk/${params.id}`} />}
      </Route>
      <Route path="/scenarios">
        {() => <RouteBoundary label="Scenarios"><TestAgent /></RouteBoundary>}
      </Route>
      <Route path="/scenarios/:id">
        {(params) => (
          <RouteBoundary label="Scenarios">
            <TestAgent callId={Number(params.id)} />
          </RouteBoundary>
        )}
      </Route>
      {/* Backward compat: keep /test-agent links working. */}
      <Route path="/test-agent">
        {() => <RouteBoundary label="Test Agent"><TestAgent /></RouteBoundary>}
      </Route>
      <Route path="/test-agent/:id">
        {(params) => (
          <RouteBoundary label="Test Agent">
            <TestAgent callId={Number(params.id)} />
          </RouteBoundary>
        )}
      </Route>
      <Route path="/settings">
        {() => <RouteBoundary label="Settings"><Settings /></RouteBoundary>}
      </Route>
      <Route path="/settings/:section">
        {(params) => (
          <RouteBoundary label="Settings">
            <Settings section={params.section} />
          </RouteBoundary>
        )}
      </Route>
      <Route path="/flows/:id">
        {(params) => (
          <RouteBoundary label="Flow Review">
            <FlowReview id={params.id} />
          </RouteBoundary>
        )}
      </Route>
      {/* Dev-only: visual bench for advisor card variants. Renders every
       * variant with seed data shaped to match the mockup states so we
       * can headless-screenshot it and diff against the mockup. */}
      <Route path="/dev/advice-bench">
        {() => <RouteBoundary label="Advice Bench"><AdviceBench /></RouteBoundary>}
      </Route>
      <Route component={Home} />
    </Switch>
  );
}

type AppLink = {
  href: string;
  title: string;
  description: string;
};

const APPS: AppLink[] = [
  {
    href: "/agent-desk",
    title: "Agent Desk",
    description:
      "Live workspace for the agent during a call. Subscriber profile loads from the active call; advisor canvas, chat, and transcript come online in upcoming phases.",
  },
  {
    href: "/inbound",
    title: "Inbound",
    description:
      "Live and historical inbound retention calls handled by the AI — full transcript, tool activity, recordings.",
  },
  {
    href: "/outbound",
    title: "Outbound",
    description:
      "Outbound retention calls run by the AI — review outcomes alongside inbound history.",
  },
  {
    href: "/scenarios",
    title: "Scenarios",
    description:
      "Reproducible end-to-end runs of named retention scenarios — a subscriber persona dials our number and the AI works the call.",
  },
];

function Home() {
  return (
    <main id="home" className="home">
      <header id="home-header" className="home-header">
        <Link id="home-settings-link" href="/settings" className="home-settings-link">
          Settings
        </Link>
      </header>

      <h1>Agent Copilot</h1>
      <p className="home-sub">
        Voice operations for retention. Pick a workspace.
      </p>

      <HomeStatusStrip />

      <ul id="home-grid" className="home-grid">
        {APPS.map((app) => {
          const slug = app.href.replace(/^\//, "");
          return (
            <li key={app.href} id={`home-card-${slug}`} className="home-card">
              <Link id={`home-card-link-${slug}`} href={app.href} className="home-card-link">
                <span className="home-card-title">{app.title} →</span>
                <span className="home-card-desc">{app.description}</span>
              </Link>
            </li>
          );
        })}
      </ul>
    </main>
  );
}

// ---------------------------------------------------------------------------
// HomeStatusStrip — factual operations data only. No aggregations, no
// historical metrics; this is what's true RIGHT NOW. Safe to show in a
// demo because nothing here can look weird.
// ---------------------------------------------------------------------------
type StatusSummary = {
  provider: string | null;
  realtimeModel: string | null;
  geminiModel: string | null;
  chainedLlm: string | null;
  activeCalls: number | null;
  subscribers: number | null;
};

function HomeStatusStrip() {
  const [s, setS] = useState<StatusSummary>({
    provider: null,
    realtimeModel: null,
    geminiModel: null,
    chainedLlm: null,
    activeCalls: null,
    subscribers: null,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [provider, realtime, gemini, chained, calls, customers] =
          await Promise.all([
            apiFetch<{ value: string }>("/api/settings/llm_provider").catch(() => null),
            apiFetch<{ value: string }>("/api/settings/model_openai_realtime").catch(() => null),
            apiFetch<{ value: string }>("/api/settings/model_gemini").catch(() => null),
            apiFetch<{ value: string }>("/api/settings/model_chained_llm").catch(() => null),
            apiFetch<{ calls: { ended_at: string | null }[] }>(
              "/api/calls?status=active&limit=50",
            ).catch(() => null),
            apiFetch<{ customers: unknown[] }>("/api/customers").catch(() => null),
          ]);
        if (cancelled) return;
        setS({
          provider: provider?.value ?? null,
          realtimeModel: realtime?.value ?? null,
          geminiModel: gemini?.value ?? null,
          chainedLlm: chained?.value ?? null,
          activeCalls: calls?.calls?.length ?? null,
          subscribers: customers?.customers?.length ?? null,
        });
      } catch {
        /* keep nulls; strip just shows muted dashes */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Pick the model that actually applies to the active provider.
  const stackModel = (() => {
    if (s.provider === "openai") return s.realtimeModel ?? "gpt-realtime";
    if (s.provider === "gemini") {
      const m = s.geminiModel ?? "gemini-live";
      return m.replace(/^models\//, "");
    }
    if (s.provider === "chained") return `chained · ${s.chainedLlm ?? "gpt-4o-mini"}`;
    return null;
  })();

  return (
    <div id="home-status" className="home-status">
      <StatusCell label="Voice stack">
        {stackModel ? (
          <>
            <span className="home-status-dot" />
            {stackModel}
          </>
        ) : (
          "—"
        )}
      </StatusCell>
      <StatusCell label="Active calls">
        {s.activeCalls === null ? "—" : s.activeCalls}
      </StatusCell>
      <StatusCell label="Subscribers">
        {s.subscribers === null ? "—" : s.subscribers}
      </StatusCell>
    </div>
  );
}

function StatusCell({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  const slug = label.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  return (
    <div id={`home-status-cell-${slug}`} className="home-status-cell">
      <span className="home-status-label">{label}</span>
      <span className="home-status-value">{children}</span>
    </div>
  );
}
